// /CalculationsScreen/components/ScheduleCalculationTable/index.tsx
import { useStore } from 'hooks/useStore';
import { ScheduleCalculationsResponse } from 'Interfaces/CalculationInterfaces/MainCalculationInterfaces';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useEffect, useState } from 'react';
import { commaSeparatedNumbers } from 'utils/commaSeparatedNumbers';
import { formatDateTime, formatDurationMinutes } from '../../utils/formatters';
import RoomHeader from '../../utils/RoomHeader';
import classes from './scheduleCalculationTable.module.scss';
import { useScheduleCalculation } from './useScheduleCalculationTable';

const DEBUG_SCHEDULE_COMPONENT = true;

interface ScheduleCalculationScreenProps {
  roomData: ScheduleCalculationsResponse;
  loading: boolean;
  expandedRows: any;
  setExpandedRows: (rows: any) => void;
}

// Body renderer functions defined outside component
const renderJobId = (rowData: any) => {
  // If it's a clean entry (has cleanId but no jobId)
  if (!rowData.jobId && rowData.cleanId) {
    return <span className={classes.cleanLabel}>{rowData.cleanId}</span>;
  }
  return rowData.jobId || '-';
};

const renderStartTime = (rowData: any) => formatDateTime(rowData.startTime);

const renderEndTime = (rowData: any) => formatDateTime(rowData.endTime);

const renderBottles = (rowData: any) => commaSeparatedNumbers(rowData.bottles) || '-';

// Higher-order function for shift cell rendering
const createShiftCellRenderer = (shiftName: string) => (rowData: any) => {
  if (!rowData.shift) {
    return '-';
  }

  const isThisShift = rowData.shift.name.toLowerCase() === shiftName?.toLowerCase();

  return (
    <div className={classes.shiftCheckmark}>
      {isThisShift ? (
        <span className={classes.checkmark}>✓</span>
      ) : (
        <span className={classes.dash}>-</span>
      )}
    </div>
  );
};

const ScheduleCalculationScreen: React.FC<ScheduleCalculationScreenProps> = ({
  roomData,
  loading,
  expandedRows,
  setExpandedRows
}) => {
  const { formatDuration } = useScheduleCalculation();
  const [expandedRooms, setExpandedRooms] = useState<Record<number, boolean>>({});
  const { roomStore } = useStore();

  // Add a state to track if we've received a response
  const [hasReceivedResponse, setHasReceivedResponse] = useState(false);

  // Reset the response flag when loading changes to true
  useEffect(() => {
    if (loading === true) {
      setHasReceivedResponse(false);
    }
  }, [loading]);

  // Set the response flag when we receive data (when loading becomes false)
  useEffect(() => {
    if (loading === false) {
      setHasReceivedResponse(true);
    }
  }, [loading]);

  // Ensure room data is loaded
  useEffect(() => {
    if (roomStore.rooms.length === 0) {
      roomStore.fetchRooms(1, 100);
    }
  }, []);

  // Expand all accordions whenever roomData changes
  useEffect(() => {
    if (roomData && roomData.length > 0) {
      // Create object with all rooms expanded
      const expandAll: Record<number, boolean> = {};
      roomData.forEach(room => {
        expandAll[room.id] = true;
      });
      setExpandedRooms(expandAll);
    }
  }, [roomData]);

  // Check if roomData has calculations property
  const hasValidData = () => {
    const result =
      roomData &&
      roomData.length > 0 &&
      roomData.some(room => room.calculations && room.calculations.length > 0);

    return result;
  };

  // Toggle room expansion
  const toggleRoom = (roomId: number) => {
    setExpandedRooms(prev => ({
      ...prev,
      [roomId]: !prev[roomId]
    }));
  };

  // Show loading spinner if:
  // 1. loading is true, OR
  // 2. We don't have a response yet
  if (loading === true || !hasReceivedResponse) {
    return (
      <div className={classes.loadingContainer}>
        <ProgressSpinner className={classes.spinner} />
        <div>Loading schedule calculation data...</div>
      </div>
    );
  }

  // Only show "No data found" when:
  // 1. We've received a response (hasReceivedResponse is true)
  // 2. We're not loading
  // 3. We don't have valid data
  if (hasReceivedResponse && loading === false && !hasValidData()) {
    return (
      <div className={classes.emptyMessage}>
        No schedule data found. Please check your filters or try again later.
      </div>
    );
  }

  return (
    <div className={classes.scheduleCalculationContainer}>
      {roomData.map(room => (
        <div key={room.id} className={classes.roomSection}>
          <RoomHeader
            name={room.name}
            roomId={room.id}
            isExpanded={!!expandedRooms[room.id]}
            onToggle={() => toggleRoom(room.id)}
          />
          {expandedRooms[room.id] && (
            <DataTable
              value={room.calculations}
              emptyMessage="No records found"
              className={classes.dataTable}
              rowClassName={rowData =>
                `${classes.tableRow} ${!rowData.jobId ? classes.cleanRow : ''}`
              }
              resizableColumns
              columnResizeMode="expand"
              scrollable
              scrollHeight="flex"
            >
              <Column
                field="jobId"
                header="Job ID"
                className={classes.column}
                body={renderJobId}
              />
              <Column
                field="startTime"
                header="Start Time"
                className={classes.column}
                body={renderStartTime}
              />
              <Column
                field="duration"
                header="Duration"
                className={classes.column}
                body={rowData => formatDurationMinutes(rowData.duration)}
              />
              <Column
                field="endTime"
                header="End Time"
                className={classes.column}
                body={renderEndTime}
              />
              <Column
                field="shift.name"
                header="First"
                className={`${classes.column} ${classes.shiftColumn}`}
                body={createShiftCellRenderer('First')}
              />
              <Column
                field="shift.name"
                header="Second"
                className={`${classes.column} ${classes.shiftColumn}`}
                body={createShiftCellRenderer('Second')}
              />
              {/* 
              <Column
                field=""
                header=""
                className={classes.column}
                body={rowData => rowData. || '-'}
              />
              */}

              <Column
                field="bottles"
                header="Bottles"
                className={classes.column}
                body={rowData => {
                  if (rowData.bottles === undefined || rowData.bottles === null) {
                    return '-';
                  }
                  // Round to whole number before formatting
                  const roundedBottles = Math.round(rowData.bottles);
                  return commaSeparatedNumbers(roundedBottles);
                }}
              />
            </DataTable>
          )}
        </div>
      ))}
    </div>
  );
};

export default ScheduleCalculationScreen;
